import { Button, Spinner } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast, ToastContainer } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { Stepper } from "@/app/components/initiate/Stepper";
import { BigProfileCreation } from "@/api/businessProfiles";
import { BusinessProfileForm } from "@/app/components/businessProfile/BusinessProfileForm";
import { LocationForm } from "@/app/components/businessProfile/LocationForm";
import { RequisitesForm } from "@/app/components/businessProfile/RequisitesForm";
import { BaseLayout } from "@/app/layout/baseLayout";

export function CreateBusinessProfilePage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("hash") || "no-code";
  const handleError = useErrorHandling();
  const navigate = useNavigate();

  const isAuthorized = localStorage.getItem("access_token") !== null;
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfileData, setIsLoadingProfileData] = useState(false);
  const [profileData, setProfileData] = useState<Partial<BigProfileCreation>>(
    {},
  );

  const steps = [
    {
      name: "Авторизация",
      description: "Необходима для создания бизнес-профиля",
      longDescription:
        "Для продолжения работы вам необходимо обзавестись аккаунтом в нашей системе.",
      hideTitle: true,
    },
    {
      name: "Бизнес-профиль",
      description: "Основная информация о компании",
      longDescription:
        "Сейчас вам нужно ввести данные о вашем бизнес-профиле. Бизнес-профиль - портфель, объединяющий все локации (объекты), которыми вы управляете.",
    },
    {
      name: "Локация",
      description: "Информация о местоположении",
      longDescription:
        "Давайте создадим первую локацию. Это - аналог физического здания, который содержит в себе коммерческие помещения (к примеру, офисы).",
    },
    {
      name: "Реквизиты",
      description: "Банковские реквизиты компании",
      longDescription:
        "Сейчас вам нужно ввести банковские реквизиты вашей компании. Они нужны для корректного создания договор с вашими будущими арендаторами и автоматизации выставления счетов.",
    },
    {
      name: "Проверка",
      description: "Проверьте введенную информацию",
      longDescription:
        "Проверьте все введенные данные перед созданием бизнес-профиля.",
      hideTitle: true,
    },
  ];

  // Загрузка данных профиля при наличии кода
  useEffect(() => {
    if (code !== "no-code" && isAuthorized) {
      setIsLoadingProfileData(true);
      api.businessProfiles
        .getBigProfileProps(code)
        .then((response) => {
          setProfileData(response.data);
          console.log("Loaded profile data:", response.data);
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        })
        .finally(() => {
          setIsLoadingProfileData(false);
        });
    }
  }, [code, isAuthorized, handleError]);

  const changeStep = useCallback(
    (step: number) => {
      if (step > activeStep || step == 0) {
        toast.info("Вы не можете перейти к этому шагу");
        return;
      }
      if (step == activeStep) return;

      setActiveStep(step);
      console.log("Current profileData:", profileData);
    },
    [activeStep, profileData],
  );

  const handleBusinessProfileSubmit = useCallback(
    (data: Partial<BigProfileCreation>) => {
      const updatedData = {
        ...profileData,
        ...data,
      };
      setProfileData(updatedData);
      setActiveStep(2);
      console.log("Updated profileData:", updatedData);
    },
    [profileData],
  );

  const handleLocationSubmit = useCallback(
    (data: Partial<BigProfileCreation>) => {
      const updatedData = {
        ...profileData,
        ...data,
      };
      setProfileData(updatedData);
      setActiveStep(3);
      console.log("Updated profileData:", updatedData);
    },
    [profileData],
  );

  const handleRequisitesSubmit = useCallback(
    (data: Partial<BigProfileCreation>) => {
      const updatedData = {
        ...profileData,
        ...data,
      };
      setProfileData(updatedData);
      setActiveStep(4);
      console.log("Updated profileData:", updatedData);
    },
    [profileData],
  );

  const handleFinalSubmit = useCallback(() => {
    console.log("Final profileData:", profileData);

    setIsLoading(true);
    api.businessProfiles
      .createBigProfile(
        profileData as BigProfileCreation,
        code !== "no-code" ? code : undefined,
      )
      .then(() => {
        toast.success("Бизнес-профиль успешно создан");
        navigate(`/dashboard/`);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [profileData, navigate, handleError, code]);

  useEffect(() => {
    if (isAuthorized) {
      setActiveStep(1);
    }
  }, [isAuthorized]);

  return (
    <BaseLayout>
      {(isLoading || isLoadingProfileData) && (
        <div className="w-screen h-dvh min-h-dvh flex flex-col items-center justify-center">
          <Spinner />
        </div>
      )}
      {!isLoading && !isLoadingProfileData && (
        <>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
          <div className="w-screen h-dvh min-h-dvh flex flex-col items-center">
            <div className="w-screen max-w-screen-2xl flex flex-col p-4 gap-4 items-center lg:pt-16">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4 w-full mb-8">
                <div className="flex flex-col lg:flex-row items-center gap-4">
                  <img
                    src="/logo.svg"
                    alt="Logo"
                    className="h-8 lg:h-12 w-auto"
                  />
                  <div className="flex flex-col items-center lg:items-start gap-1">
                    <h1 className="text-xl lg:text-2xl font-bold text-foreground text-center lg:text-left">
                      Создание бизнес-профиля
                    </h1>
                    <p className="text-xs lg:text-sm text-gray-500 text-center lg:text-left">
                      Пройдите несколько шагов для начала работы с нашей
                      платформой
                    </p>
                  </div>
                </div>
                <Button
                  variant="solid"
                  size="sm"
                  className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                  onClick={() => navigate("/auth/login")}
                >
                  У меня уже есть профиль
                </Button>
              </div>

              <div className="flex flex-col lg:flex-row gap-4 w-full">
                <div className="lg:w-1/3">
                  <Stepper
                    steps={steps}
                    activeStep={activeStep}
                    changeStep={changeStep}
                  />
                </div>

                <div className="lg:w-2/3">
                  <div className="w-full h-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
                    <div className="flex flex-col gap-4">
                      {!steps[activeStep].hideTitle && (
                        <div className="flex flex-col gap-2">
                          <span className="text-lg font-semibold">
                            {steps[activeStep].name}
                          </span>
                          <span className="text-sm text-gray-500">
                            {steps[activeStep].longDescription}
                          </span>
                        </div>
                      )}

                      {activeStep == 0 && (
                        <div className="w-full flex flex-col items-center gap-8 py-8">
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-elastic-50 dark:bg-elastic-900/30 rounded-full flex items-center justify-center">
                              <svg
                                className="w-8 h-8 text-elastic-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                              </svg>
                            </div>
                            <div className="text-center">
                              <h2 className="text-2xl font-bold text-foreground mb-2">
                                Требуется регистрация
                              </h2>
                              <p className="text-gray-600 dark:text-gray-400 max-w-md">
                                Для создания бизнес-профиля необходимо
                                зарегистрироваться в системе.
                                <br />
                                Это абсолютно бесплатно и займет не более 1
                                минуты.
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                            <Button
                              variant="solid"
                              size="lg"
                              className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium flex-1"
                              onClick={() =>
                                navigate(
                                  `/auth/register?redirect_to=${encodeURIComponent(window.location.pathname)}`,
                                )
                              }
                            >
                              Создать аккаунт
                            </Button>
                            <Button
                              variant="flat"
                              size="lg"
                              className="flex-1"
                              onClick={() =>
                                navigate(
                                  `/auth/login?redirect_to=${encodeURIComponent(window.location.pathname)}`,
                                )
                              }
                            >
                              У меня уже есть аккаунт
                            </Button>
                          </div>
                        </div>
                      )}
                      {activeStep == 1 && (
                        <div className="w-full flex flex-col gap-4">
                          <BusinessProfileForm
                            defaults={profileData}
                            onSubmit={handleBusinessProfileSubmit}
                          />
                        </div>
                      )}
                      {activeStep == 2 && (
                        <div className="w-full flex flex-col gap-4">
                          <LocationForm
                            defaults={profileData}
                            onSubmit={handleLocationSubmit}
                          />
                        </div>
                      )}
                      {activeStep == 3 && (
                        <div className="w-full flex flex-col gap-4">
                          <RequisitesForm
                            defaults={profileData}
                            onSubmit={handleRequisitesSubmit}
                          />
                        </div>
                      )}
                      {activeStep == 4 && (
                        <div className="w-full flex flex-col gap-6">
                          <div className="flex flex-col gap-2">
                            <h2 className="text-2xl font-bold text-foreground">
                              Проверьте введенную информацию
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400">
                              Убедитесь, что все данные корректны перед
                              созданием бизнес-профиля
                            </p>
                          </div>
                          <div className="flex flex-col gap-6">
                            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-foreground">
                                  Бизнес-профиль
                                </h3>
                                <Button
                                  size="sm"
                                  variant="flat"
                                  onPress={() => setActiveStep(1)}
                                >
                                  Редактировать
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Название компании
                                  </span>
                                  <p className="font-medium">
                                    {profileData.business_display_name ||
                                      "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    ИНН
                                  </span>
                                  <p className="font-medium">
                                    {profileData.inn || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    ОГРН
                                  </span>
                                  <p className="font-medium">
                                    {profileData.ogrn || "Не указано"}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-foreground">
                                  Локация
                                </h3>
                                <Button
                                  size="sm"
                                  variant="flat"
                                  onPress={() => setActiveStep(2)}
                                >
                                  Редактировать
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Название локации
                                  </span>
                                  <p className="font-medium">
                                    {profileData.location_display_name ||
                                      "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Город
                                  </span>
                                  <p className="font-medium">
                                    {profileData.city || "Не указано"}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <span className="text-sm text-gray-500">
                                    Адрес
                                  </span>
                                  <p className="font-medium">
                                    {profileData.address || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Станция метро
                                  </span>
                                  <p className="font-medium">
                                    {profileData.metro_station || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Район
                                  </span>
                                  <p className="font-medium">
                                    {profileData.district || "Не указано"}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-foreground">
                                  Реквизиты
                                </h3>
                                <Button
                                  size="sm"
                                  variant="flat"
                                  onPress={() => setActiveStep(3)}
                                >
                                  Редактировать
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Название реквизитов
                                  </span>
                                  <p className="font-medium">
                                    {profileData.req_display_name ||
                                      "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    КПП
                                  </span>
                                  <p className="font-medium">
                                    {profileData.kpp || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Номер счета
                                  </span>
                                  <p className="font-medium">
                                    {profileData.bank_account || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    БИК
                                  </span>
                                  <p className="font-medium">
                                    {profileData.bik || "Не указано"}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <span className="text-sm text-gray-500">
                                    Название банка
                                  </span>
                                  <p className="font-medium">
                                    {profileData.bank_name || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Телефон
                                  </span>
                                  <p className="font-medium">
                                    {profileData.phone || "Не указано"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Email
                                  </span>
                                  <p className="font-medium">
                                    {profileData.email || "Не указано"}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <span className="text-sm text-gray-500">
                                    ФИО владельца
                                  </span>
                                  <p className="font-medium">
                                    {profileData.req_owner_name || "Не указано"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-4 justify-end">
                            <Button
                              variant="light"
                              onPress={() => setActiveStep(3)}
                            >
                              Назад
                            </Button>
                            <Button
                              className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                              onClick={handleFinalSubmit}
                            >
                              Создать бизнес-профиль
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </BaseLayout>
  );
}
